/**
 * Wedding hours are wall-clock at the venue and carry no timezone.
 *
 * A planner saying "four to eleven" means four to eleven where the wedding is;
 * there is no second observer for whom that is a different number. This is why
 * the times live in their own `time` columns rather than inside `wedding_date`,
 * whose date-only, UTC-rendered invariant they would otherwise break.
 *
 * The same reasoning is why an end earlier than its start is not an error here:
 * a reception that goes to half past midnight ends at `00:30` on the next
 * calendar day, and no timezone or date arithmetic is involved in saying so.
 */

export interface TimeOfDay {
  hour: number;
  minute: number;
}

/** `'16:00'` and `'16:00:00'` both mean 4pm. Postgres returns the latter. */
export function parseTimeOfDay(raw: string): TimeOfDay | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(raw.trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/** The `HH:MM` the API and a `time` column both accept. */
export function formatTimeValue({ hour, minute }: TimeOfDay): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** `'16:00'` → `'4:00 PM'`. Null for anything that is not a time. */
export function formatTimeOfDay(raw: string): string | null {
  const t = parseTimeOfDay(raw);
  if (!t) return null;
  const suffix = t.hour < 12 ? 'AM' : 'PM';
  // 0 and 12 both display as 12 — midnight is 12 AM, noon is 12 PM.
  const hour12 = t.hour % 12 === 0 ? 12 : t.hour % 12;
  return `${hour12}:${String(t.minute).padStart(2, '0')} ${suffix}`;
}

export function minutesSinceMidnight({ hour, minute }: TimeOfDay): number {
  return hour * 60 + minute;
}

export function timeOfDayFromMinutes(minutes: number): TimeOfDay {
  // Wraps, so callers can add an offset to a start time without checking
  // whether it walked off the end of the day.
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  return { hour: Math.floor(wrapped / 60), minute: wrapped % 60 };
}

/**
 * How long the day runs, in minutes, reading an end at or before the start as
 * the next day. Null when either side is missing or unparseable; 0 when the two
 * are the same time, which is a planner mid-edit rather than a 24-hour wedding.
 */
export function elapsedMinutes(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): number | null {
  const start = startTime ? parseTimeOfDay(startTime) : null;
  const end = endTime ? parseTimeOfDay(endTime) : null;
  if (!start || !end) return null;
  const diff = minutesSinceMidnight(end) - minutesSinceMidnight(start);
  return diff < 0 ? diff + 1440 : diff;
}

/**
 * `'7 hr'`, `'4 hr 45 min'`, `'45 min'`.
 *
 * Exact on purpose: a 6:15 start to an 11:00 finish is four and three quarter
 * hours, and rounding it to five would quietly disagree with the two times
 * sitting right above it.
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (!hours) return `${mins} min`;
  return mins ? `${hours} hr ${mins} min` : `${hours} hr`;
}

/**
 * `"4:00 PM – 11:00 PM"`, or `"from 4:00 PM"` when the end is unknown.
 *
 * Returns null when there is no start: an end time alone is not something the
 * schema permits, and rendering "until 11 PM" for it would paper over a bug.
 */
export function formatWeddingHours(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): string | null {
  const start = startTime ? formatTimeOfDay(startTime) : null;
  if (!start) return null;
  const end = endTime ? formatTimeOfDay(endTime) : null;
  return end ? `${start} – ${end}` : `from ${start}`;
}
